// "Complete the set" on top product pages (snippets/complete-the-set.liquid).
// Follows the colour and size picked in the main product form, matches the pant and jacket to them,
// and adds the ticked pieces to the cart in one go.
const JACKET_SIZES = { XXS: 'XXS', XS: 'XS/S', S: 'XS/S', M: 'M/L', L: 'M/L', XL: 'XL/XXL', XXL: 'XL/XXL' };

class CompleteTheSet extends HTMLElement {
  #data = {};
  #strings = {};
  #lastId = '';
  #jacketTouched = false;
  #timer = 0;

  connectedCallback() {
    try {
      this.#data = JSON.parse(this.querySelector('[data-set-products]').textContent);
      this.#strings = JSON.parse(this.dataset.strings || '{}');
    } catch {
      this.hidden = true;
      return;
    }
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-jacket-colour]')) this.#jacketTouched = true;
      this.#render();
    });
    this.querySelector('[data-add]')?.addEventListener('click', () => this.#add());
    this.#fillJacketColours();
    this.#render();
    // The main form re-renders on variant change, so check the selected variant regularly.
    this.#timer = window.setInterval(() => {
      if (this.#currentId() !== this.#lastId) this.#render();
    }, 400);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #currentId() {
    const input = document.querySelector('#product-main-form input[name="id"]');
    return input ? String(input.value) : '';
  }

  #opt(product, variant, name) {
    const index = product.options.indexOf(name);
    return index === -1 ? null : variant[3 + index];
  }

  #find(product, wants) {
    return product.variants.find((v) =>
      Object.entries(wants).every(([name, value]) => {
        const index = product.options.indexOf(name);
        return index === -1 || value == null || v[3 + index] === value;
      }),
    );
  }

  #colours(product) {
    const index = product.options.indexOf('Color');
    if (index === -1) return [];
    return [...new Set(product.variants.map((v) => v[3 + index]))];
  }

  #money(cents) {
    // Reuse the store's own money format: the sample is 1234.56 written the store's way.
    const sample = this.dataset.moneySample || '';
    const match = sample.match(/1[.,\s]?234([.,])56/);
    if (match) {
      const sep = match[1];
      const whole = Math.floor(cents / 100).toString();
      const thousands = sep === ',' ? '.' : ',';
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return sample.replace(match[0], `${grouped}${sep}${String(cents % 100).padStart(2, '0')}`);
    }
    const currency = window.Shopify?.currency?.active || 'CAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #fillJacketColours() {
    const jacket = this.#data[this.dataset.jacket];
    const select = this.querySelector('[data-jacket-colour]');
    if (!jacket || !select) return;
    const retired = this.dataset.retired || '';
    this.#colours(jacket)
      .filter((c) => !retired.includes(`|${c}|`))
      .forEach((c) => select.append(new Option(c, c)));
  }

  #render() {
    const top = this.#data[this.dataset.top];
    if (!top) return;
    this.#lastId = this.#currentId();
    const topVariant = top.variants.find((v) => String(v[0]) === this.#lastId) || top.variants[0];
    const colour = this.#opt(top, topVariant, 'Color');
    const size = this.#opt(top, topVariant, 'Size');

    const pieces = [];
    pieces.push({ el: this.querySelector('[data-piece="top"]'), product: top, variant: topVariant, colour, meta: [colour, size] });

    const pantEl = this.querySelector('[data-piece="pant"]');
    const pant = this.#data[this.dataset.pant];
    if (pantEl && pant) {
      const length = pantEl.querySelector('[data-length]')?.value || null;
      const hasColour = this.#colours(pant).includes(colour);
      const variant = hasColour ? this.#find(pant, { Size: size, Color: colour, Length: length }) : null;
      const status = !hasColour
        ? this.#strings.noColour.replace('[colour]', colour)
        : !variant ? this.#strings.noSize.replace('[size]', size) : '';
      pieces.push({ el: pantEl, product: pant, variant, colour, meta: [colour, size, length], status });
    }

    const jacketEl = this.querySelector('[data-piece="jacket"]');
    const jacket = this.#data[this.dataset.jacket];
    if (jacketEl && jacket) {
      const select = jacketEl.querySelector('[data-jacket-colour]');
      if (select && !this.#jacketTouched) {
        // Match the top when the jacket comes in that colour, otherwise Black.
        if (!this.#colours(jacket).includes(colour)) {
          // nothing to add
        } else if (![...select.options].some((o) => o.value === colour)) {
          select.append(new Option(colour, colour));
        }
        const wanted = [...select.options].some((o) => o.value === colour) ? colour : 'Black';
        if ([...select.options].some((o) => o.value === wanted)) select.value = wanted;
      }
      const jacketColour = select?.value || colour;
      const jacketSize = JACKET_SIZES[size];
      const variant = jacketSize ? this.#find(jacket, { Size: jacketSize, Color: jacketColour }) : null;
      const status = !variant ? this.#strings.noSize.replace('[size]', size) : '';
      pieces.push({ el: jacketEl, product: jacket, variant, colour: jacketColour, meta: [jacketSize || size], status });
    }

    let total = 0;
    let count = 0;
    this.pieces = [];
    for (const piece of pieces) {
      const { el, product, variant } = piece;
      const box = el.querySelector('[data-pick]');
      const statusEl = el.querySelector('[data-status]');
      const img = el.querySelector('[data-img]');
      const available = Boolean(variant && variant[1]);
      let status = piece.status || '';
      if (variant && !variant[1]) status = this.#strings.soldOut;

      const src = product.images?.[piece.colour] || product.image;
      if (img && src && img.getAttribute('src') !== src) img.src = src;
      if (img) img.alt = `${product.title} – ${piece.colour}`;
      el.querySelector('[data-price]').textContent = variant ? this.#money(variant[2]) : '';
      el.querySelector('[data-meta]').textContent = piece.meta.filter(Boolean).join(' · ');
      statusEl.textContent = status;
      statusEl.hidden = !status;
      box.disabled = !available;
      if (!available) box.checked = false;
      el.classList.toggle('opacity-50', !available);

      if (available && box.checked) {
        total += variant[2];
        count += 1;
        this.pieces.push(variant[0]);
      }
    }

    this.querySelector('[data-total]').textContent = this.#money(total);
    const label = this.querySelector('[data-add-label]');
    const button = this.querySelector('[data-add]');
    if (!button.hasAttribute('aria-busy')) {
      label.textContent = count === 0 ? this.#strings.addNone : (count === 1 ? this.#strings.addOne : this.#strings.add.replace('[count]', count));
    }
    button.disabled = count === 0;
  }

  async #add() {
    const button = this.querySelector('[data-add]');
    const label = this.querySelector('[data-add-label]');
    const errorEl = this.querySelector('[data-error]');
    const ids = this.pieces || [];
    if (!ids.length) return;
    errorEl.hidden = true;
    button.setAttribute('aria-busy', 'true');
    label.textContent = this.#strings.adding;
    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })) },
          { event: { context: 'product', detail: { source: 'luuna-complete-set' } } },
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);
        label.textContent = this.#strings.added;
        await window.Shopify.actions.openCart?.();
      } else {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || this.#strings.error);
        }
        window.location.href = this.dataset.cartUrl;
        return;
      }
    } catch (error) {
      errorEl.textContent = error?.message || this.#strings.error;
      errorEl.hidden = false;
    } finally {
      window.setTimeout(() => {
        button.removeAttribute('aria-busy');
        this.#render();
      }, 1800);
    }
  }
}

if (!customElements.get('complete-the-set')) customElements.define('complete-the-set', CompleteTheSet);
